import React, { useContext, useState } from 'react'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import Button from '@components/Button'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  TEXT_SIZES,
  LABELS,
  BUTTON_TYPE,
} from '@helpers/constants'
import Text from '@components/Text'
import RadioButtonScale from '@components/RadioButtonScale'
import Form from '@components/Form'
import { useForm } from 'react-hook-form'
import { useCreateFeedback } from '@hooks/mutations/useCreateFeedback'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { forms, questionTypes } from './data'
import { useItemSettings } from '@hooks/useItemSettings'
import EmojiRadioButtonScale from '@components/EmojiRadioButtonScale'
import ButtonWithLoader from '@components/ButtonWithLoader'

const FeedbackPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    panels: {
      feedback: { id, type },
    },
  } = state
  const [showSuccess, setShowSuccess] = useState(false)
  const { handleSubmit, register, formState, errors } = useForm()
  const { isDirty } = formState
  const { currentUser } = useCurrentUser()
  const { post, isPosting } = useCreateFeedback()
  const formData =
    forms[currentUser.role_id === '3' ? 'student' : 'teacher'][type]
  const { schoolYears } = useItemSettings()

  const onSubmit = async (data) => {
    const res = await post({
      feedbackable: {
        connect: {
          type: type === 'book' ? `App\\Models\\Book` : `App\\Models\\Section`,
          id: parseInt(id),
        },
      },
      feedbackQuestions: {
        create: formData.questions.map((question) => {
          let answer = ''

          if (question.type === questionTypes.yearSelect) {
            answer = schoolYears.find(
              (year) => year.id === data[question.name]
            ).name
          } else if (question.type === questionTypes.checkboxes) {
            answer = data[question.name].join()
          } else {
            answer = data[question.name]
          }

          return {
            question: question.question,
            answer: answer,
          }
        }),
      },
      customer: { connect: parseInt(currentUser.id) },
    })

    if (res?.success) {
      setShowSuccess(true)
    }
  }

  if (!formData) return null

  return (
    <>
      <div className={'flex justify-between'}>
        <Text size={TEXT_SIZES.BASE}>{formData.title}</Text>
        <Button
          handleClick={() => {
            dispatch(closePanel(PANELS.FEEDBACK))
          }}
          size={BUTTON_SIZE.NO_PADDING}
          icon={'Close'}
        />
      </div>

      {!showSuccess && (
        <Form>
          <Divider size={DIVIDER_SIZE.BASE} />

          {formData.questions.map((question) => (
            <div key={question.name}>
              <Text size={TEXT_SIZES.SMALL} bold>
                {question.question}
              </Text>

              {question.type == questionTypes.checkboxes && (
                <div className={'mb-12'}>
                  {question.options.map((option, index) => {
                    return (
                      <Form.Checkbox
                        key={index}
                        label={option}
                        value={option}
                        name={question.name}
                        ref={register({ required: false })}
                      />
                    )
                  })}
                </div>
              )}

              {question.type == questionTypes.emojiRating && (
                <div className={'mb-12'}>
                  <EmojiRadioButtonScale
                    name={question.name}
                    innerRef={register({ required: false })}
                  />
                  {errors[question.name] && (
                    <Form.ErrorMessage>
                      Du måste göra ett val för att kunna skicka in
                    </Form.ErrorMessage>
                  )}
                </div>
              )}

              {question.type == questionTypes.rating && (
                <div className={'mb-12'}>
                  <RadioButtonScale
                    name={question.name}
                    minLabel={question.minLabel}
                    maxLabel={question.maxLabel}
                    innerRef={register({ required: false })}
                  />
                  {errors[question.name] && (
                    <Form.ErrorMessage>
                      Du måste göra ett val för att kunna skicka in
                    </Form.ErrorMessage>
                  )}
                </div>
              )}

              {question.type == questionTypes.text && (
                <div className={'mt-2 mb-12'}>
                  <Form.TextArea
                    name={question.name}
                    ref={register({ required: false })}
                    // label={question.question}
                  />
                </div>
              )}

              {question.type == questionTypes.yearSelect && (
                <div className={'mt-2 mb-12'}>
                  <Form.Select
                    name={question.name}
                    ref={register({ required: false })}
                    options={schoolYears.map((year) => ({
                      value: year.id,
                      name: year.name,
                    }))}
                  />
                </div>
              )}
            </div>
          ))}

          <div className={'flex justify-center items-center'}>
            <ButtonWithLoader
              type={BUTTON_TYPE.PRIMARY}
              size={BUTTON_SIZE.LARGE}
              isLoading={isPosting}
              disabledText={'Skicka meddelande'}
              enabledText={'Skicka meddelande'}
              disabled={!isDirty}
              handleClick={handleSubmit(onSubmit)}
            />
          </div>
        </Form>
      )}
      {showSuccess && (
        <div className={'flex flex-col items-center'}>
          <Divider size={DIVIDER_SIZE.LARGE} />
          <Heading headingLevel={HEADING_TYPE.H1} size={TEXT_SIZES.LARGE}>
            Tack för ditt meddelande!
          </Heading>

          <Divider size={DIVIDER_SIZE.BASE} />

          <Button
            handleClick={() => {
              dispatch(closePanel(PANELS.FEEDBACK))
            }}
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
          >
            Stäng
          </Button>
        </div>
      )}
    </>
  )
}

export default FeedbackPanel
